import * as React from 'react'
import { ChevronDown, Search } from 'lucide-react'
import { EventOrderCancellation } from '@/components/events/event-order-cancellation'
import { EventOrderSeats } from '@/components/events/event-order-seats'
import { EventSeatAnswers } from '@/components/events/event-seat-answers'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  SortableTableHead,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { cn } from '@/lib/utils'
import { OwnedChip } from '@/components/OwnedChip'
import { OwnerFilter } from '@/components/OwnerFilter'
import { OwnedBanner } from '@/components/OwnedBanner'
import { formatMoneyCents } from '@/lib/money'
import type { EventOrder, BuyerGroup } from './event-orders'

export function OrdersToolbar({
  search,
  invoiceStatus,
  onSearch,
  onInvoiceStatus,
  owner,
  onOwner,
  action,
}: {
  search: string
  invoiceStatus: string
  onSearch: (value: string) => void
  onInvoiceStatus: (value: string) => void
  owner?: string
  onOwner: (value?: string) => void
  action?: React.ReactNode
}) {
  return (
    <div className="mb-3 flex flex-wrap items-center gap-2">
      <div className="relative w-full max-w-xs">
        <Search
          className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground"
          aria-hidden="true"
        />
        <Input
          className="h-8 pl-8 text-caption"
          placeholder="Search buyer or attendee"
          value={search}
          onChange={(event) => onSearch(event.target.value)}
        />
      </div>
      <Select value={invoiceStatus} onValueChange={onInvoiceStatus}>
        <SelectTrigger aria-label="Invoice" className="h-8 w-36 text-caption">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">Invoice · All</SelectItem>
          <SelectItem value="paid">Paid</SelectItem>
          <SelectItem value="partially_paid">Partially paid</SelectItem>
          <SelectItem value="unpaid">Unpaid</SelectItem>
          <SelectItem value="cancelled">Cancelled</SelectItem>
        </SelectContent>
      </Select>
      <OwnerFilter value={owner} onChange={onOwner} />
      {action && <div className="ml-auto">{action}</div>}
    </div>
  )
}

export function OrdersHeader({
  sort,
  direction,
  onSort,
}: {
  sort: 'placed' | 'total'
  direction: 'asc' | 'desc'
  onSort: (sort: 'placed' | 'total') => void
}) {
  return (
    <TableHeader>
      <TableRow>
        <TableHead>Buyer</TableHead>
        <TableHead>Seats</TableHead>
        <SortableTableHead
          active={sort === 'placed'}
          direction={direction === 'asc' ? 'ascending' : 'descending'}
          onToggle={() => onSort('placed')}
        >
          Placed
        </SortableTableHead>
        <TableHead>Invoice</TableHead>
        <SortableTableHead
          active={sort === 'total'}
          direction={direction === 'asc' ? 'ascending' : 'descending'}
          onToggle={() => onSort('total')}
          className="text-right"
        >
          Total
        </SortableTableHead>
        <TableHead className="relative w-12">
          <span className="sr-only">Actions</span>
        </TableHead>
      </TableRow>
    </TableHeader>
  )
}

export function OrderRows({
  groups,
  eventId,
  expandedOrderId,
  onExpandedOrderChange,
  readOnly,
}: {
  groups: BuyerGroup[]
  eventId: number
  expandedOrderId?: number
  onExpandedOrderChange: (id: number | undefined) => void
  readOnly: boolean
}) {
  return (
    <TableBody>
      {groups.flatMap((group) =>
        group.orders.map((order, index) => (
          <OrderRowsForGroup
            key={order.id}
            group={group}
            order={order}
            showGroup={index === 0}
            eventId={eventId}
            expanded={expandedOrderId === order.id}
            onExpandedOrderChange={onExpandedOrderChange}
            readOnly={readOnly}
          />
        )),
      )}
    </TableBody>
  )
}

function OrderRowsForGroup({
  group,
  order,
  showGroup,
  eventId,
  expanded,
  onExpandedOrderChange,
  readOnly,
}: {
  group: BuyerGroup
  order: EventOrder
  showGroup: boolean
  eventId: number
  expanded: boolean
  onExpandedOrderChange: (id: number | undefined) => void
  readOnly: boolean
}) {
  return (
    <React.Fragment>
      {showGroup && (
        <TableRow className="bg-muted/40 hover:bg-muted/40">
          <TableCell colSpan={6} className="py-1.5 text-caption text-muted-foreground">
            {group.buyer.name} · {group.orders.length}{' '}
            {group.orders.length === 1 ? 'order' : 'orders'}
          </TableCell>
        </TableRow>
      )}
      <TableRow
        className={cn(
          order.money_state?.status === 'open' && 'bg-primary/5 hover:bg-primary/10',
          order.status === 'cancelled' && 'text-muted-foreground',
        )}
      >
        <TableCell>
          <p className="font-medium">
            {group.buyer.name} <OwnedChip owner={order.owner} />
          </p>
          <p className="text-caption text-muted-foreground">{group.buyer.email}</p>
        </TableCell>
        <TableCell>{order.quantity}</TableCell>
        <TableCell>{`${order.ordered_date} · ${order.ordered_time} ${order.time_zone_label}`}</TableCell>
        <TableCell>
          <InvoiceStatus order={order} />
        </TableCell>
        <TableCell className="text-right font-semibold tabular-nums">
          {order.invoice?.total ?? formatMoneyCents(0, order.currency)}
        </TableCell>
        <TableCell className="text-right">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-8 w-8 px-0"
            aria-label={`${expanded ? 'Collapse' : 'Expand'} order for ${group.buyer.name}`}
            aria-expanded={expanded}
            onClick={() => onExpandedOrderChange(expanded ? undefined : order.id)}
          >
            <ChevronDown
              className={cn('size-4 transition-transform', expanded && 'rotate-180')}
              strokeWidth={1.5}
              aria-hidden="true"
            />
          </Button>
        </TableCell>
      </TableRow>
      {expanded && (
        <TableRow className="bg-muted/30 hover:bg-muted/30">
          <TableCell colSpan={6} className="p-0">
            <ExpandedOrderPanel eventId={eventId} order={order} readOnly={readOnly} />
          </TableCell>
        </TableRow>
      )}
    </React.Fragment>
  )
}

function InvoiceStatus({ order }: { order: EventOrder }) {
  const status = order.invoice?.status ?? 'Free order'
  const variant: 'success' | 'neutral' | 'warning' =
    status === 'Paid' ? 'success' : status === 'Cancelled' ? 'neutral' : 'warning'
  return <Badge variant={variant}>{status}</Badge>
}

function ExpandedOrderPanel({
  eventId,
  order,
  readOnly,
}: {
  eventId: number
  order: EventOrder
  readOnly: boolean
}) {
  const cancellable = order.status !== 'cancelled' && !readOnly && !isOwnedOrder(order)
  const answered =
    order.answers.length > 0 || (order.seats ?? []).some((seat) => seat.answers.length > 0)
  return (
    <div className="sticky left-3 mx-3 my-2 space-y-3 rounded-md border border-border bg-background px-4 py-3 max-md:max-w-[calc(100vw-3.5rem)]">
      <OwnedBanner record={order} />
      <EventOrderSeats order={order} />
      {(answered || cancellable || order.status === 'cancelled') && (
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div className="space-y-2 text-body-sm">
            {order.answers.map((answer) => (
              <p key={answer.prompt} className="text-muted-foreground">
                <span className="font-medium text-foreground">{answer.prompt}</span> ·{' '}
                {answer.value}
              </p>
            ))}
            <EventSeatAnswers seats={order.seats ?? []} />
          </div>
          {order.status === 'cancelled' ? (
            <span className="text-caption text-muted-foreground">Cancelled</span>
          ) : (
            cancellable && <EventOrderCancellation eventId={eventId} order={order} />
          )}
        </div>
      )}
    </div>
  )
}

function isOwnedOrder(order: EventOrder) {
  return Boolean(order.owner && typeof order.owner !== 'string' && order.owner.connection)
}
